import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  X, Phone, FileText, ListChecks, Edit3, Copy, Lightbulb,
  Play, LogOut, Clock, Ban, AlertTriangle, CalendarClock, UserCog,
  ExternalLink, Receipt,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useGetPatientQuery, useGetPatientOverviewQuery, useUpdatePatientEngagementMutation,
  useGetBookableProceduresQuery,
} from '../../api/patientsApi';
import { openPatientDetail } from '../ui/detailPanelSlice';
import { usePatientSessionSummaryQuery } from '../../api/sessionPackagesApi';
import {
  useUpdateAppointmentStatusMutation,
  useExtendAppointmentMutation,
  useRescheduleAppointmentMutation,
  useReassignAppointmentMutation,
  useUpdateAppointmentProcedureMutation,
  useListAppointmentsQuery,
} from '../../api/appointmentsApi';
import { useListDoctorsQuery } from '../../api/usersApi';
import { apiErrorMessage, isPermissionError } from '../../utils/apiError';
import { useIsMobile } from '../../hooks/useIsMobile';
import { colorForDoctor, doctorDisplayName } from './doctorColors';
import { STATUS_META, statusLabel, canExtend, procedureLabel } from './appointmentStatus';
import RenewalPitchModal, { isRenewalPitchRequired } from '../patients/RenewalPitchModal';
import { AddNewReceiptModal } from '../billing/BillingPage';
import FirstApptAttributionModal from './FirstApptAttributionModal';

const fmtINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);

function fmtDuration(mins) {
  const m = Math.max(0, Number(mins) || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h} hr ${r} min`;
  if (h) return `${h} hr`;
  return `${r} min`;
}

const EXTENSION_OPTIONS = [15, 30, 60];

const normalizePhone = (raw) => {
  if (!raw) return '';
  const text = String(raw).trim();
  if (!text) return '';
  const digits = text.replace(/\D/g, '');
  if (!digits) return '';
  return text.startsWith('+') ? `+${digits}` : digits;
};

export default function AppointmentPopover({
  appointment,
  doctorName,
  allAppointments = [],   // passed in from AppointmentsPage for conflict checks
  onClose,
  onCollectPayment,
  onEdit,
}) {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const [showReschedule, setShowReschedule] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [showClearDues, setShowClearDues] = useState(false);
  const [updateStatus] = useUpdateAppointmentStatusMutation();
  // Quick-update of engagement flags directly from the calendar popover — saves the
  // round-trip to the patient profile when staff just want to tick a box.
  const [updateEngagement, { isLoading: savingEngagement }] = useUpdatePatientEngagementMutation();
  const [pitchPrompt, setPitchPrompt] = useState(null); // holds the pending status to retry
  // Same retry-after-modal pattern as pitchPrompt, but for the No Show/Cancelled marketing-
  // attribution prompt (holds 'NoShow' or 'Cancelled' while the modal is open).
  const [attributionPrompt, setAttributionPrompt] = useState(null);
  // Active packages with remaining capacity — lets the clinician pick which package this
  // check-out draws from (treatment shifts / mid-stream renewals). Defaults to FIFO server-side.
  const { data: sessionSummary } = usePatientSessionSummaryQuery(appointment.patientId, { skip: !appointment.patientId });
  const activePackages = (sessionSummary?.packages ?? [])
    .filter((p) => p.status === 'Active' && (p.totalSessions - p.sessionsUsed) > 0);
  const [pkgId, setPkgId] = useState('');

  // Which billed procedure this visit is for. Distinct from the "Draw from" package picker above:
  // this is the durable attribution stored on the appointment (drives the label, the per-procedure
  // ledger and the doctor's revenue rate), whereas "Draw from" only steers which package a
  // check-out decrements. It is also the way historical appointments — deliberately left showing
  // the generic "Session" rather than being backfilled — get corrected.
  const { data: bookableProcedures = [] } = useGetBookableProceduresQuery(
    appointment.patientId, { skip: !appointment.patientId },
  );
  const [updateProcedure, { isLoading: savingProcedure }] = useUpdateAppointmentProcedureMutation();

  const handleProcedureChange = async (treatmentTypeId) => {
    try {
      const chosen = bookableProcedures.find((p) => p.treatmentTypeId === treatmentTypeId);
      await updateProcedure({
        id: appointment.id,
        patientId: appointment.patientId,
        treatmentTypeId,
        treatmentTypeName: chosen?.name ?? null,
      }).unwrap();
      toast.success(treatmentTypeId ? `Session type set to ${chosen?.name}` : 'Session type cleared');
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const [extendAppointment] = useExtendAppointmentMutation();
  const [rescheduleAppointment, { isLoading: rescheduling }] = useRescheduleAppointmentMutation();
  const [reassignAppointment, { isLoading: reassigning }] = useReassignAppointmentMutation();
  const { data: doctors = [] } = useListDoctorsQuery();
  const { data: patient } = useGetPatientQuery(appointment.patientId, { skip: !appointment.patientId });
  // Session + billing snapshot for this patient (remaining sessions, due/overdue).
  const { data: overview } = useGetPatientOverviewQuery(appointment.patientId, { skip: !appointment.patientId });
  // How many appointments this patient has ever had — to flag a first visit / new patient.
  const { data: apptHistory } = useListAppointmentsQuery(
    { patientId: appointment.patientId, page: 1, pageSize: 1 }, { skip: !appointment.patientId });
  const apptCount = apptHistory?.totalCount;
  // A patient with no prior appointments (only this one) is on their first visit.
  const isFirstVisit = apptCount != null && apptCount <= 1;
  // New cohort: server flag (1 appointment OR 0 sessions OR 0 billings), with first-visit as a fallback.
  const isNewPatient = appointment.patientIsNew || isFirstVisit;

  const color = colorForDoctor({
    id: appointment.assignedUserId,
    colorTag: appointment.assignedUserColorTag,
  });
  const initials = (patient?.fullName ?? appointment.patientName ?? 'P')
    .split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const profileComplete = patient?.dateOfBirth && patient?.address;
  const time = new Date(appointment.scheduledAt);
  const originalDuration = appointment.originalDurationMinutes ?? appointment.durationMinutes ?? 30;
  const duration = appointment.durationMinutes ?? 30;
  const wasExtended = duration > originalDuration;

  const statusMeta = STATUS_META[appointment.status] ?? STATUS_META.Scheduled;
  const phoneRaw = patient?.phoneNumber ?? appointment.phoneNumber ?? '';
  const waNumber = normalizePhone(phoneRaw);
  const waLink = waNumber ? `https://wa.me/${waNumber.replace(/^\+/, '')}` : '';
  // Click-to-call: on a phone this opens the native dialer; on desktop (no tel: handler → the
  // browser would log a "scheme not registered" error) we copy the number to the clipboard instead.
  const callPatient = () => {
    if (!waNumber) return;
    if (isMobile) {
      window.location.href = `tel:${waNumber}`;
    } else if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(waNumber).then(
        () => toast.success('Number copied'),
        () => toast.info(waNumber),
      );
    } else {
      toast.info(waNumber);
    }
  };

  // One-way status locks: each action is only valid from specific predecessor states.
  const s = appointment.status;
  const engageCheck = s === 'Scheduled'
    ? { ok: true }
    : { ok: false, reason: s === 'InProgress' ? 'Already engaged' : `Cannot engage — status is ${statusLabel(s)}` };
  const checkoutCheck = s === 'InProgress'
    ? { ok: true }
    : { ok: false, reason: s === 'Completed' ? 'Already checked out' : s === 'Scheduled' ? 'Patient must be engaged first' : `Cannot check out — status is ${statusLabel(s)}` };
  const noShowCheck = (s === 'Scheduled' || s === 'InProgress')
    ? { ok: true }
    : { ok: false, reason: `Cannot mark no-show — status is ${statusLabel(s)}` };
  const cancelCheck = (s === 'Scheduled' || s === 'InProgress')
    ? { ok: true }
    : { ok: false, reason: `Cannot cancel — status is ${statusLabel(s)}` };

  const handleCopyCode = async () => {
    if (!patient?.patientCode) return;
    try { await navigator.clipboard.writeText(patient.patientCode); toast.success('Copied!'); }
    catch { toast.info(patient.patientCode); }
  };

  const runStatusUpdate = async (status, successMsg) => {
    try {
      // For check-out, pass the chosen package so the session draws from (and is labelled by) it.
      const body = { id: appointment.id, status };
      if (status === 'Completed' && pkgId) body.sessionPackageId = pkgId;
      await updateStatus(body).unwrap();
      toast.success(successMsg);
      onClose();
    } catch (e) {
      // Compulsory renewal-pitch guard on the patient's 2nd-to-last session checkout.
      if (isRenewalPitchRequired(e)) { setPitchPrompt(status); return; }
      toast.error(e?.data?.error?.message ?? e?.data?.message ?? 'Could not update status');
    }
  };

  const handleEngagementFlag = async (patch) => {
    if (!appointment.patientId) return;
    try { await updateEngagement({ id: appointment.patientId, ...patch }).unwrap(); }
    catch (e) {
      if (isPermissionError(e)) return; // benign quick-toggle — don't nag about permission
      toast.error(e?.data?.error?.message || 'Could not update flag');
    }
  };

  // First appointment + not yet attributed → capture marketing source before the No Show/Cancelled
  // status lands, since neither ever raises an invoice (the only other place attribution is asked).
  // Leaves the Completed path (handleCheckout, above) untouched — that one is gated by billing.
  const needsAttribution = isFirstVisit && !!appointment.patientId && !patient?.marketingSource;
  const runOrPromptAttribution = (status, successMsg) => {
    if (needsAttribution) setAttributionPrompt(status);
    else runStatusUpdate(status, successMsg);
  };

  const handleEngage   = () => engageCheck.ok   && runStatusUpdate('InProgress', 'Patient engaged');
  const handleCheckout = () => checkoutCheck.ok && runStatusUpdate('Completed',  'Checked out');
  const handleNoShow   = () => noShowCheck.ok   && runOrPromptAttribution('NoShow',     'Marked as No Show');
  const handleCancel   = () => cancelCheck.ok   && runOrPromptAttribution('Cancelled',  'Appointment cancelled');

  const handleReschedule = async ({ newScheduledAt, newDurationMinutes }) => {
    try {
      await rescheduleAppointment({
        id: appointment.id,
        newScheduledAt,
        newDurationMinutes,
        reason: 'Rescheduled from calendar',
      }).unwrap();
      toast.success('Appointment rescheduled');
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not reschedule'));
    }
  };

  const handleReassign = async (newAssignedUserId, reason) => {
    if (!newAssignedUserId || newAssignedUserId === appointment.assignedUserId) { setShowReassign(false); return; }
    try {
      await reassignAppointment({ id: appointment.id, newAssignedUserId, reason }).unwrap();
      toast.success('Doctor reassigned');
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not reassign doctor'));
    }
  };

  const handleExtend = async (addMinutes) => {
    const check = canExtend(appointment, addMinutes, allAppointments);
    if (!check.ok) { toast.error(check.reason); return; }
    try {
      await extendAppointment({
        id: appointment.id,
        // PUT /appointments/:id replaces all fields — pass current values back unchanged
        // alongside the new duration so we don't accidentally clear them on the server.
        assignedUserId:  appointment.assignedUserId,
        scheduledAt:     appointment.scheduledAt,
        durationMinutes: check.newDurationMinutes,
        notes:           [appointment.notes, `[Extended +${addMinutes} min]`].filter(Boolean).join('\n'),
      }).unwrap();
      toast.success(`Extended by ${addMinutes} min`);
      onClose();
    } catch (e) {
      const msg = e?.data?.message ?? e?.data?.title ?? e?.error ?? 'Could not extend appointment';
      toast.error(msg);
    }
  };

  // Tiny helper to render a workflow button with a tooltip-reason when disabled
  const WfButton = ({ icon: Icon, label, check, onClick, variant }) => (
    <button
      type="button"
      className={`wf-btn wf-btn--${variant}`}
      onClick={onClick}
      disabled={!check.ok}
      title={check.ok ? label : check.reason}
    >
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div className="cal-popover-overlay" onClick={onClose}>
      {pitchPrompt && appointment.patientId && (
        <RenewalPitchModal
          patientId={appointment.patientId}
          patientName={patient?.fullName ?? appointment.patientName}
          onClose={() => setPitchPrompt(null)}
          onSaved={() => { const s = pitchPrompt; setPitchPrompt(null); runStatusUpdate(s, 'Checked out'); }}
        />
      )}
      {attributionPrompt && appointment.patientId && (
        <FirstApptAttributionModal
          patientId={appointment.patientId}
          patientName={patient?.fullName ?? appointment.patientName}
          targetStatus={attributionPrompt}
          onClose={() => setAttributionPrompt(null)}
          onSaved={() => {
            const s = attributionPrompt;
            setAttributionPrompt(null);
            runStatusUpdate(s, s === 'NoShow' ? 'Marked as No Show' : 'Appointment cancelled');
          }}
        />
      )}
      <div className="cal-popover" onClick={(e) => e.stopPropagation()}>
        <div className="cal-popover-status-dot" style={{ background: statusMeta.color }} title={statusLabel(appointment.status)} />
        <div className="cal-popover-actions">
          <button title="Copy ID" onClick={handleCopyCode}><Copy size={14} /></button>
          <button title="Edit" onClick={onEdit}><Edit3 size={14} /></button>
          <button title="Close" onClick={onClose}><X size={14} /></button>
        </div>

        <div className="cal-popover-top">
          <div className="cal-popover-avatar">{initials}</div>
          <div className="cal-popover-meta">
            <div className="cal-popover-name">
              {isNewPatient && <span className="cal-new-flag" title="New patient">(N)</span>}
              {appointment.patientId ? (
                <button
                  type="button"
                  onClick={() => { dispatch(openPatientDetail(appointment.patientId)); onClose(); }}
                  className="cal-popover-name-link"
                  title="Open patient profile"
                >
                  {patient?.fullName ?? appointment.patientName ?? appointment.leadName ?? 'Patient'}
                  <ExternalLink size={12} style={{ marginLeft: 4, opacity: 0.7 }} />
                </button>
              ) : (
                patient?.fullName ?? appointment.patientName ?? appointment.leadName ?? 'Patient'
              )}
            </div>
            <div className="cal-popover-code">
              {patient?.patientCode ?? '—'}
              <span
                className="cal-status-pill"
                style={{ background: statusMeta.bg, color: statusMeta.text, marginLeft: 8 }}
              >
                {statusLabel(appointment.status)}
              </span>
            </div>
            {isNewPatient && (
              <div className="cal-popover-flags">
                <span className="cal-flag cal-flag--new">★ New patient</span>
                {isFirstVisit && <span className="cal-flag cal-flag--first">First appointment</span>}
              </div>
            )}
            <div className="cal-popover-sub">
              {!profileComplete && <>Profile incomplete.</>}
            </div>
          </div>
        </div>

        <div className="cal-popover-rows">
          <div className="cal-popover-row">
            <Phone size={14} />
            <span>{phoneRaw || '—'}</span>
            <span style={{ display: 'inline-flex', gap: 6, marginLeft: 'auto' }}>
              {waNumber && (
                <button
                  type="button"
                  className="cal-popover-call"
                  onClick={callPatient}
                  aria-label={`Call ${patient?.fullName ?? 'patient'}`}
                  title={isMobile ? 'Call patient' : 'Copy number'}
                >
                  <Phone size={16} />
                </button>
              )}
              {waLink && (
                <a className="wa-link" href={waLink} target="_blank" rel="noreferrer" aria-label="Open WhatsApp chat">
                  <svg className="wa-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.52 3.48A11.9 11.9 0 0 0 12.01 0C5.4 0 .03 5.36 0 11.97c0 2.11.55 4.17 1.6 5.99L0 24l6.2-1.63a11.9 11.9 0 0 0 5.8 1.47h.01c6.61 0 11.98-5.36 11.99-11.97a11.9 11.9 0 0 0-3.48-8.39zm-8.51 18.3h-.01a10 10 0 0 1-5.1-1.4l-.37-.22-3.68.97.98-3.59-.24-.37a9.96 9.96 0 0 1-1.53-5.2C2.07 6.49 6.55 2 12.01 2c2.66 0 5.16 1.03 7.04 2.91A9.9 9.9 0 0 1 22 11.96c-.01 5.47-4.5 9.82-9.98 9.82zm5.5-7.34c-.3-.15-1.77-.87-2.05-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-1.77-.87-2.93-1.56-4.1-3.54-.31-.52.31-.48.9-1.6.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.48-.5-.67-.5-.17 0-.37-.02-.57-.02-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.5.71.31 1.26.5 1.69.64.71.23 1.36.2 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
                  </svg>
                </a>
              )}
            </span>
          </div>
          <div className="cal-popover-row">
            <FileText size={14} /> <span className="muted">Notes: {appointment.notes ? '' : 'None'}</span>
            {appointment.notes && <span style={{ marginLeft: 4 }}>{appointment.notes}</span>}
          </div>
        </div>

        {/* Engagement flags quick-update — the source of truth is the Patient record,
            so checking these here updates the same fields shown on the patient profile. */}
        {patient && (
          <div className="cal-popover-rows" style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 8, gap: 4 }}>
            <label className="cal-popover-row" style={{ cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={!!patient.googleReviewDone}
                disabled={savingEngagement}
                onChange={(e) => handleEngagementFlag({ googleReviewDone: e.target.checked })}
              />
              <span>Google Review Done</span>
            </label>
            <label className="cal-popover-row" style={{ cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={!!patient.feedbackFormCompleted}
                disabled={savingEngagement}
                onChange={(e) => handleEngagementFlag({ feedbackFormCompleted: e.target.checked })}
              />
              <span>Feedback Form Completed</span>
            </label>
          </div>
        )}

        {/* Sessions + billing snapshot for this patient */}
        {overview && (
          <div className="cal-popover-rows" style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 8 }}>
            <div className="cal-popover-row">
              <ListChecks size={14} />
              <span className="muted">Sessions:</span>&nbsp;
              <strong>{overview.sessionsUsed ?? 0}/{overview.totalSessions ?? 0}</strong>
              <span style={{ marginLeft: 6, color: (overview.remainingSessions ?? 0) <= 0 ? 'var(--red-700)' : 'var(--green-700)' }}>
                · {overview.remainingSessions ?? 0} left
              </span>
            </div>
            <div className="cal-popover-row">
              <FileText size={14} />
              <span className="muted">Due:</span>&nbsp;
              <strong style={{ color: (overview.outstanding ?? 0) > 0 ? 'var(--red-700)' : 'var(--green-700)' }}>
                {fmtINR(overview.outstanding)}
              </strong>
              {(overview.overdueAmount ?? 0) > 0 && (
                <span style={{ marginLeft: 6, color: 'var(--red-700)', fontWeight: 600 }}>· overdue {fmtINR(overview.overdueAmount)}</span>
              )}
              {(overview.advanceBalance ?? 0) > 0 && (
                <span style={{ marginLeft: 6, color: 'var(--blue-700)' }}>· advance {fmtINR(overview.advanceBalance)}</span>
              )}
            </div>
          </div>
        )}

        <div className="cal-popover-appt">
          <div className="cal-popover-appt-info">
            <div className="cal-popover-appt-info-title">
              <span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: 4, background: color.bg, border: `1px solid ${color.border}` }} />
              {procedureLabel(appointment) || 'In-Clinic Appointment'}
            </div>
            <div className="cal-popover-appt-info-detail">
              with {doctorName ? doctorDisplayName(doctorName) : 'Doctor'} at {time.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })} for {fmtDuration(duration)}
              {wasExtended && (
                <span style={{ marginLeft: 6, color: 'var(--amber-600)', fontWeight: 600 }}>
                  · extended from {fmtDuration(originalDuration)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Workflow actions ───────────────────────────────────────────── */}
        <div className="cal-popover-workflow">
          {/* Session type — the procedure this visit is booked against. Shown whenever the patient
              has anything bookable, including for already-completed visits, because this is how a
              historical "Session" row gets attributed after the fact. */}
          {appointment.patientId && bookableProcedures.length > 0 && (
            <div className="cal-wf-row" style={{ marginBottom: 8 }}>
              <span className="cal-wf-extend-label">Session type</span>
              <select
                className="form-control"
                style={{ height: 30, padding: '0 24px 0 8px', fontSize: '0.8rem', flex: 1 }}
                value={appointment.treatmentTypeId ?? ''}
                disabled={savingProcedure}
                onChange={(e) => handleProcedureChange(e.target.value || null)}
                title="Which billed procedure this appointment is for"
              >
                <option value="">Session (not specified)</option>
                {bookableProcedures.map((p) => (
                  <option key={p.treatmentTypeId} value={p.treatmentTypeId}>
                    {p.name} · {p.remaining} left{p.onLatestBill ? '' : ' (earlier bill)'}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* Package picker — only when the patient holds more than one active package, so the
              clinician maps this check-out to the treatment actually being utilized. */}
          {appointment.status !== 'Completed' && activePackages.length > 1 && (
            <div className="cal-wf-row" style={{ marginBottom: 8 }}>
              <span className="cal-wf-extend-label">Draw from</span>
              <select
                className="form-control"
                style={{ height: 30, padding: '0 24px 0 8px', fontSize: '0.8rem', flex: 1 }}
                value={pkgId}
                onChange={(e) => setPkgId(e.target.value)}
                title="Which package this session is drawn from"
              >
                <option value="">Auto (oldest active)</option>
                {activePackages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {(p.treatmentTypeName || p.name)} · {p.totalSessions - p.sessionsUsed} left
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="cal-wf-row">
            <WfButton
              icon={Play}     label="Engage"   variant="primary"
              check={engageCheck}   onClick={handleEngage}
            />
            <WfButton
              icon={LogOut}   label="Checkout" variant="success"
              check={checkoutCheck} onClick={handleCheckout}
            />
            <WfButton
              icon={AlertTriangle} label="No Show" variant="warn"
              check={noShowCheck}  onClick={handleNoShow}
            />
            <WfButton
              icon={Ban}      label="Cancel"   variant="danger"
              check={cancelCheck}   onClick={handleCancel}
            />
          </div>

          {/* Extension row — only meaningful when not already checked out / cancelled */}
          {appointment.status !== 'Completed' && appointment.status !== 'Cancelled' && appointment.status !== 'NoShow' && (
            <div className="cal-wf-row cal-wf-extend">
              <span className="cal-wf-extend-label"><Clock size={13} /> Extend</span>
              {EXTENSION_OPTIONS.map((m) => {
                const check = canExtend(appointment, m, allAppointments);
                return (
                  <button
                    key={m}
                    type="button"
                    className="wf-btn wf-btn--ghost"
                    onClick={() => handleExtend(m)}
                    disabled={!check.ok}
                    title={check.ok ? `Add ${m} minutes` : check.reason}
                  >
                    +{m} min
                  </button>
                );
              })}
              <button
                type="button"
                className="wf-btn wf-btn--ghost"
                style={{ marginLeft: 'auto' }}
                onClick={() => setShowReschedule(true)}
                title="Move this appointment to a new date/time"
              >
                <CalendarClock size={14} /> Reschedule
              </button>
            </div>
          )}

          {/* Reassign doctor — available regardless of status (keeps the same slot). */}
          {appointment.status !== 'Cancelled' && (
            <div className="cal-wf-row">
              <button
                type="button"
                className="wf-btn wf-btn--ghost"
                onClick={() => setShowReassign((v) => !v)}
                title="Assign this appointment to a different doctor"
              >
                <UserCog size={14} /> Reassign doctor
              </button>
            </div>
          )}
        </div>

        {showReschedule && (
          <RescheduleForm
            appointment={appointment}
            busy={rescheduling}
            onCancel={() => setShowReschedule(false)}
            onSubmit={handleReschedule}
          />
        )}

        {showReassign && (
          <ReassignForm
            appointment={appointment}
            doctors={doctors}
            busy={reassigning}
            onCancel={() => setShowReassign(false)}
            onSubmit={handleReassign}
          />
        )}

        {appointment.status !== 'Completed' && (
          <div className="cal-popover-promo">
            <Lightbulb size={20} style={{ flexShrink: 0 }} />
            <span>Collect payments online from patients anytime! <a href="#get">Get it now</a></span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cal-popover-pay" style={{ flex: 1 }} onClick={() => onCollectPayment(appointment, patient)}>
            Collect Payment
          </button>
          {appointment.patientId && (
            <button
              type="button"
              className="cal-popover-pay"
              style={{ flex: 1, background: 'var(--amber-500)' }}
              onClick={() => setShowClearDues(true)}
              title="Record a receipt against this patient's latest invoice"
            >
              <Receipt size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} /> Clear Dues
            </button>
          )}
        </div>
      </div>

      {showClearDues && (
        <AddNewReceiptModal
          initialPatientId={appointment.patientId}
          initialPatientName={patient?.fullName ?? appointment.patientName}
          onClose={() => setShowClearDues(false)}
          onCreated={() => setShowClearDues(false)}
        />
      )}
    </div>
  );
}

/** Inline reschedule form: pick a new date/time + duration; original date is preserved as audit. */
function RescheduleForm({ appointment, busy, onCancel, onSubmit }) {
  const start = new Date(appointment.scheduledAt);
  const pad = (n) => String(n).padStart(2, '0');
  const [date, setDate] = useState(`${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`);
  const [time, setTime] = useState(`${pad(start.getHours())}:${pad(start.getMinutes())}`);
  const [duration, setDuration] = useState(appointment.durationMinutes ?? 30);

  const submit = (e) => {
    e.preventDefault();
    if (!date || !time) return;
    const [hh, mm] = time.split(':');
    const dt = new Date(date);
    dt.setHours(Number(hh) || 0, Number(mm) || 0, 0, 0);
    onSubmit({ newScheduledAt: dt.toISOString(), newDurationMinutes: Number(duration) || 30 });
  };

  return (
    <div className="cal-popover-workflow" style={{ background: 'var(--gray-50)' }}>
      <form onSubmit={submit}>
        <div className="cal-form-sched-row" style={{ marginBottom: 8 }}>
          <input className="form-control" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <span className="joiner">at</span>
          <input className="form-control" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          <span className="joiner">for</span>
          <select className="form-control" value={duration} onChange={(e) => setDuration(e.target.value)}>
            <option value={15}>15 min</option>
            <option value={30}>30 min</option>
            <option value={45}>45 min</option>
            <option value={60}>60 min</option>
            <option value={90}>90 min</option>
          </select>
        </div>
        <div className="cal-wf-row">
          <button type="submit" className="wf-btn wf-btn--primary" disabled={busy}>
            {busy ? 'Rescheduling…' : 'Confirm reschedule'}
          </button>
          <button type="button" className="wf-btn wf-btn--ghost" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

/** Inline reassign form: pick a different doctor; the slot/time is preserved. */
function ReassignForm({ appointment, doctors, busy, onCancel, onSubmit }) {
  const [doctorId, setDoctorId] = useState(appointment.assignedUserId || '');
  const [reason, setReason] = useState('');

  const submit = (e) => {
    e.preventDefault();
    onSubmit(doctorId, reason.trim() || null);
  };

  return (
    <div className="cal-popover-workflow" style={{ background: 'var(--gray-50)' }}>
      <form onSubmit={submit}>
        <div style={{ marginBottom: 8 }}>
          <select className="form-control" value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
            <option value="">— Select doctor —</option>
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>{doctorDisplayName(d.fullName)}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 8 }}>
          <input
            className="form-control"
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="cal-wf-row">
          <button type="submit" className="wf-btn wf-btn--primary" disabled={busy || !doctorId || doctorId === appointment.assignedUserId}>
            {busy ? 'Reassigning…' : 'Confirm reassign'}
          </button>
          <button type="button" className="wf-btn wf-btn--ghost" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
